import React, { useEffect, useState } from 'react';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import {
  adminAdjustCredits,
  adminConfirmUserEmail,
  adminDeactivateUser,
  adminDeleteUser,
  adminExportUserData,
  adminOverrideSubscription,
  adminSendResetPassword,
  adminUpdateUserRoles,
} from '../../../services/adminService';
import './UserDetailDrawer.css';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const Row = ({ label, children }) => (
  <div className="user-drawer-row">
    <span className="user-drawer-row-label">{label}</span>
    <span className="user-drawer-row-value">{children ?? '—'}</span>
  </div>
);

// Three role strings live in the BE allowlist (User, Admin, CRM); BE rejects anything else.
const ALLOWED_ROLES = ['User', 'Admin', 'CRM'];

// Known production plans (id, label) — hard-coded for the override dropdown.
const KNOWN_PLANS = [
  { id: '4d7379a1-aae4-485e-bf76-879af3984d14', label: 'Lunar — 99 RON / lună' },
  { id: '1c66f817-56f2-4a4b-b997-8b887a7998da', label: 'Anual — 990 RON / an' },
];

const toLocalDatetimeInput = (date) => {
  // <input type="datetime-local"> expects 'YYYY-MM-DDTHH:mm' in *local* time.
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
};

const UserDetailDrawer = ({ user, isOpen, onClose, onChanged }) => {
  // Which action's confirmation modal is open, if any.
  // 'confirm-email' | 'send-reset' | 'edit-roles' | 'adjust-credits'
  //   | 'override-subscription' | 'deactivate' | 'delete' | null
  const [pendingAction, setPendingAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'ok' | 'err', message }
  const [draftRoles, setDraftRoles] = useState([]);

  // Phase 4 — credit adjust draft.
  const [creditDraft, setCreditDraft] = useState({ amount: 0, category: 'subscription' });

  // Phase 4 — subscription override draft (start = now, end = now + 30d).
  const buildSubscriptionDraft = () => {
    const now = new Date();
    const end = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return {
      subscriptionId: KNOWN_PLANS[0].id,
      startDate: toLocalDatetimeInput(now),
      endDate: toLocalDatetimeInput(end),
      paymentStatus: 'paid',
    };
  };
  const [subDraft, setSubDraft] = useState(buildSubscriptionDraft());

  // Phase 5 — export is fire-and-forget, no modal. We surface a busy flag so
  // the button shows "Se exportă…" while the request is in flight.
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape' && !pendingAction) onClose?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose, pendingAction]);

  useEffect(() => {
    // Reset feedback when the drawer reopens for a different user.
    if (isOpen && user) setFeedback(null);
  }, [isOpen, user?.id]);

  const closeModal = () => {
    if (submitting) return;
    setPendingAction(null);
    setDraftRoles([]);
    setCreditDraft({ amount: 0, category: 'subscription' });
    setSubDraft(buildSubscriptionDraft());
  };

  const flashOk = (message) => {
    setFeedback({ type: 'ok', message });
    setTimeout(() => setFeedback(null), 5000);
  };

  const flashErr = (message) => {
    setFeedback({ type: 'err', message });
  };

  const handleConfirmEmail = async ({ reason }) => {
    setSubmitting(true);
    try {
      const r = await adminConfirmUserEmail(user.id, { reason });
      if (r?.success) {
        flashOk(r.alreadyConfirmed ? 'Email-ul era deja confirmat.' : 'Email-ul a fost confirmat.');
        onChanged?.();
        setPendingAction(null);
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReset = async ({ reason }) => {
    setSubmitting(true);
    try {
      const r = await adminSendResetPassword(user.id, { reason });
      if (r?.success) {
        flashOk('Email-ul de resetare a fost trimis.');
        setPendingAction(null);
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveRoles = async ({ reason }) => {
    setSubmitting(true);
    try {
      const r = await adminUpdateUserRoles(user.id, { roles: draftRoles, reason });
      if (r?.success) {
        flashOk('Rolurile au fost actualizate.');
        onChanged?.();
        setPendingAction(null);
        setDraftRoles([]);
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdjustCredits = async ({ reason }) => {
    setSubmitting(true);
    try {
      const r = await adminAdjustCredits(user.id, {
        amount: Number(creditDraft.amount),
        category: creditDraft.category,
        reason,
      });
      if (r?.success) {
        flashOk('Creditele au fost ajustate.');
        onChanged?.();
        setPendingAction(null);
        setCreditDraft({ amount: 0, category: 'subscription' });
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverrideSubscription = async ({ reason }) => {
    setSubmitting(true);
    try {
      const startIso = new Date(subDraft.startDate).toISOString();
      const endIso = new Date(subDraft.endDate).toISOString();
      const r = await adminOverrideSubscription(user.id, {
        subscriptionId: subDraft.subscriptionId,
        startDate: startIso,
        endDate: endIso,
        paymentStatus: subDraft.paymentStatus,
        reason,
      });
      if (r?.success) {
        flashOk('Abonamentul a fost suprascris.');
        onChanged?.();
        setPendingAction(null);
        setSubDraft(buildSubscriptionDraft());
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async ({ reason }) => {
    setSubmitting(true);
    try {
      const r = await adminDeactivateUser(user.id, { reason });
      if (r?.success) {
        flashOk('Contul a fost dezactivat.');
        onChanged?.();
        setPendingAction(null);
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async ({ reason }) => {
    setSubmitting(true);
    try {
      const r = await adminDeleteUser(user.id, { reason });
      if (r?.success) {
        flashOk('Contul a fost șters definitiv.');
        onChanged?.();
        setPendingAction(null);
        // Close the drawer — the user no longer exists.
        setTimeout(() => onClose?.(), 600);
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    setFeedback({ type: 'ok', message: 'Se exportă datele…' });
    try {
      const r = await adminExportUserData(user.id);
      if (r?.success) {
        flashOk('Export complet. Verifică folderul Downloads.');
      } else {
        flashErr('Export eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Export eșuat.');
    } finally {
      setExporting(false);
    }
  };

  const startEditRoles = () => {
    setDraftRoles(
      Array.from(new Set((user.roles || []).map((r) => (r === 'Admin' ? 'Admin' : 'User')))),
    );
    setPendingAction('edit-roles');
  };

  const startAdjustCredits = () => {
    setCreditDraft({ amount: 0, category: 'subscription' });
    setPendingAction('adjust-credits');
  };

  const startOverrideSubscription = () => {
    setSubDraft(buildSubscriptionDraft());
    setPendingAction('override-subscription');
  };

  if (!isOpen || !user) return null;

  const actionGroups = [
    {
      title: 'Cont',
      items: [
        {
          label: 'Forțează confirmarea email-ului',
          disabled: false,
          title: 'Marchează adresa de email ca fiind confirmată.',
          onClick: () => setPendingAction('confirm-email'),
        },
        {
          label: 'Trimite link de resetare parolă',
          disabled: false,
          title: 'Trimite un email cu link de resetare la adresa utilizatorului.',
          onClick: () => setPendingAction('send-reset'),
        },
        {
          label: 'Editează rolurile',
          disabled: false,
          title: 'Schimbă rolurile (User / Admin / CRM).',
          onClick: startEditRoles,
        },
      ],
    },
    {
      title: 'Abonament & credite',
      items: [
        {
          label: 'Ajustează creditele',
          disabled: false,
          title: 'Adaugă sau scade din creditele de abonament / cumpărate.',
          onClick: startAdjustCredits,
        },
        {
          label: 'Suprascrie abonamentul',
          disabled: false,
          title: 'Forțează un plan / interval / status de plată pentru acest cont.',
          onClick: startOverrideSubscription,
        },
        {
          label: 'Dezactivează contul',
          disabled: false,
          title: 'Blochează login-ul; datele rămân pentru audit.',
          onClick: () => setPendingAction('deactivate'),
        },
      ],
    },
    {
      title: 'GDPR',
      items: [
        {
          label: exporting ? 'Se exportă…' : 'Exportă datele (JSON)',
          disabled: exporting,
          title: 'Descarcă un bundle JSON cu toate datele utilizatorului.',
          onClick: handleExport,
        },
        {
          label: 'Șterge contul definitiv',
          disabled: false,
          title: 'Acțiune ireversibilă — datele nu se mai pot recupera.',
          onClick: () => setPendingAction('delete'),
        },
      ],
    },
  ];

  const toggleDraftRole = (role) => {
    setDraftRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  return (
    <>
      <div className="user-drawer-scrim" onClick={onClose} />
      <aside
        className="user-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-drawer-title"
      >
        <header className="user-drawer-header">
          <div className="user-drawer-header-titles">
            <h2 id="user-drawer-title">{user.name || user.email}</h2>
            <p>{user.email}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Închide" className="user-drawer-close">
            ×
          </button>
        </header>

        {feedback && (
          <div className={`user-drawer-feedback ${feedback.type === 'err' ? 'err' : 'ok'}`} role="status">
            {feedback.type === 'err' ? '⚠️ ' : '✓ '}
            {feedback.message}
          </div>
        )}

        <section className="user-drawer-section">
          <h3>Cont</h3>
          <Row label="Email">{user.email}</Row>
          <Row label="Nume">{user.name}</Row>
          <Row label="Companie">{user.company}</Row>
          <Row label="Telefon">{user.phone}</Row>
          <Row label="Roluri">
            {(user.roles || []).length
              ? (user.roles || []).map((r) => (
                  <span
                    key={r}
                    className={`admin-status ${r === 'Admin' ? 'contacted' : 'free'}`}
                    style={{ marginRight: 4 }}
                  >
                    {r.toUpperCase()}
                  </span>
                ))
              : '—'}
          </Row>
          <Row label="Email confirmat">{user.emailConfirmed ? 'Da' : 'Nu'}</Row>
          <Row label="Înregistrat">{fmtDate(user.dateCreated)}</Row>
        </section>

        <section className="user-drawer-section">
          <h3>Abonament & credite</h3>
          <Row label="Status">
            {user.hasActiveSubscription ? (
              <span className="admin-status paid">PLĂTIT</span>
            ) : (
              <span className="admin-status free">FREE</span>
            )}
          </Row>
          <Row label="Plan">{user.subscriptionPlanName}</Row>
          <Row label="Valabil până la">{fmtDate(user.subscriptionEndDate)}</Row>
          <Row label="Credite (total)">
            <strong>{user.totalCredits}</strong>{' '}
            <small style={{ color: '#94a3b8' }}>
              ({user.subscriptionCredits} abon. + {user.purchasedCredits} extra)
            </small>
          </Row>
        </section>

        <section className="user-drawer-section">
          <h3>Companie (CUI / J)</h3>
          <Row label="CUI">{user.companyDetails?.cui}</Row>
          <Row label="Nr. Reg. Com.">{user.companyDetails?.registrationNumber}</Row>
        </section>

        <section className="user-drawer-actions">
          {actionGroups.map((group) => (
            <div key={group.title} className="user-drawer-actions-group">
              <h4>{group.title}</h4>
              <div className="user-drawer-actions-row">
                {group.items.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    disabled={item.disabled}
                    title={item.title}
                    onClick={item.onClick}
                    className="user-drawer-action"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <p className="user-drawer-actions-note">
            Fiecare acțiune cere un motiv obligatoriu (jurnal de audit). Ștergerea contului
            este ireversibilă.
          </p>
        </section>

        <section className="user-drawer-section">
          <h3>Istoric audit</h3>
          <p className="user-drawer-section-empty">
            Va apărea aici când acțiunile încep să fie aplicate (faza 8 expune și un viewer dedicat{' '}
            <code>/admin/audit</code>).
          </p>
        </section>
      </aside>

      {/* ---- Confirmation modals for the 3 live Phase 3 actions ---- */}
      <ConfirmationModal
        isOpen={pendingAction === 'confirm-email'}
        onClose={closeModal}
        onConfirm={handleConfirmEmail}
        title="Confirmă email-ul utilizatorului"
        description={
          user.emailConfirmed
            ? `Email-ul ${user.email} apare deja ca fiind confirmat. Acțiunea va fi nu-op.`
            : `Marchează adresa ${user.email} ca fiind confirmată, fără să mai aștepți click-ul pe link-ul de confirmare.`
        }
        actionLabel="Confirmă email-ul"
        submitting={submitting}
      />

      <ConfirmationModal
        isOpen={pendingAction === 'send-reset'}
        onClose={closeModal}
        onConfirm={handleSendReset}
        title="Trimite email de resetare a parolei"
        description={`Generează un token de resetare și trimite link-ul către ${user.email}. Tu nu primești token-ul — utilizatorul îl deschide direct din inbox.`}
        actionLabel="Trimite email-ul"
        submitting={submitting}
      />

      <ConfirmationModal
        isOpen={pendingAction === 'edit-roles'}
        onClose={closeModal}
        onConfirm={handleSaveRoles}
        title="Editează rolurile utilizatorului"
        description={`Roluri curente: ${(user.roles || []).join(', ') || '—'}. Bifează rolurile dorite și descrie motivul schimbării.`}
        actionLabel="Salvează rolurile"
        submitting={submitting}
        canSubmitExtra={draftRoles.length > 0}
      >
        <div className="user-drawer-roles-pick">
          {ALLOWED_ROLES.map((role) => (
            <label key={role} className="user-drawer-roles-pick-item">
              <input
                type="checkbox"
                checked={draftRoles.includes(role)}
                onChange={() => toggleDraftRole(role)}
                disabled={submitting}
              />
              <span>{role}</span>
            </label>
          ))}
          {draftRoles.length === 0 && (
            <p className="user-drawer-roles-pick-warn">
              Trebuie să rămână cel puțin un rol selectat.
            </p>
          )}
        </div>
      </ConfirmationModal>

      {/* ---- Phase 4 — adjust credits ---- */}
      <ConfirmationModal
        isOpen={pendingAction === 'adjust-credits'}
        onClose={closeModal}
        onConfirm={handleAdjustCredits}
        title="Ajustează creditele utilizatorului"
        description={`Sold actual: ${user.totalCredits} (${user.subscriptionCredits} abon. + ${user.purchasedCredits} extra). Sumele negative scad creditele.`}
        actionLabel="Aplică ajustarea"
        submitting={submitting}
        canSubmitExtra={Number(creditDraft.amount) !== 0 && !!creditDraft.category}
      >
        <div className="user-drawer-form">
          <label className="user-drawer-form-field">
            <span>Categorie</span>
            <select
              value={creditDraft.category}
              onChange={(e) => setCreditDraft((d) => ({ ...d, category: e.target.value }))}
              disabled={submitting}
            >
              <option value="subscription">Abonament</option>
              <option value="purchased">Cumpărate</option>
            </select>
          </label>
          <label className="user-drawer-form-field">
            <span>Cantitate (poate fi negativă)</span>
            <input
              type="number"
              value={creditDraft.amount}
              onChange={(e) => setCreditDraft((d) => ({ ...d, amount: e.target.value }))}
              disabled={submitting}
              step="1"
            />
          </label>
          <p className="user-drawer-form-hint">
            Folosește valori negative ca să scazi din sold. Zero nu este permis.
          </p>
        </div>
      </ConfirmationModal>

      {/* ---- Phase 4 — override subscription ---- */}
      <ConfirmationModal
        isOpen={pendingAction === 'override-subscription'}
        onClose={closeModal}
        onConfirm={handleOverrideSubscription}
        title="Suprascrie abonamentul"
        description={`Setează un plan, un interval și un status de plată pentru ${user.email}. Operațiunea înlocuiește abonamentul curent.`}
        actionLabel="Salvează abonamentul"
        submitting={submitting}
        canSubmitExtra={
          !!subDraft.subscriptionId &&
          !!subDraft.startDate &&
          !!subDraft.endDate &&
          new Date(subDraft.endDate) > new Date(subDraft.startDate)
        }
      >
        <div className="user-drawer-form">
          <label className="user-drawer-form-field">
            <span>Plan</span>
            <select
              value={subDraft.subscriptionId}
              onChange={(e) => setSubDraft((d) => ({ ...d, subscriptionId: e.target.value }))}
              disabled={submitting}
            >
              {KNOWN_PLANS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="user-drawer-form-field">
            <span>Început</span>
            <input
              type="datetime-local"
              value={subDraft.startDate}
              onChange={(e) => setSubDraft((d) => ({ ...d, startDate: e.target.value }))}
              disabled={submitting}
            />
          </label>
          <label className="user-drawer-form-field">
            <span>Sfârșit</span>
            <input
              type="datetime-local"
              value={subDraft.endDate}
              onChange={(e) => setSubDraft((d) => ({ ...d, endDate: e.target.value }))}
              disabled={submitting}
            />
          </label>
          <label className="user-drawer-form-field">
            <span>Status plată</span>
            <select
              value={subDraft.paymentStatus}
              onChange={(e) => setSubDraft((d) => ({ ...d, paymentStatus: e.target.value }))}
              disabled={submitting}
            >
              <option value="paid">Plătit</option>
              <option value="pending">În așteptare</option>
              <option value="failed">Eșuat</option>
            </select>
          </label>
          {new Date(subDraft.endDate) <= new Date(subDraft.startDate) && (
            <p className="user-drawer-form-warn">
              Data de sfârșit trebuie să fie ulterioară celei de început.
            </p>
          )}
        </div>
      </ConfirmationModal>

      {/* ---- Phase 4 — deactivate account ---- */}
      <ConfirmationModal
        isOpen={pendingAction === 'deactivate'}
        onClose={closeModal}
        onConfirm={handleDeactivate}
        title="Dezactivează contul"
        description={`Utilizatorul ${user.email} nu va mai putea face login. Datele rămân stocate pentru audit și pot fi reactivate manual.`}
        actionLabel="Dezactivează contul"
        submitting={submitting}
      />

      {/* ---- Phase 5 — hard delete account ---- */}
      <ConfirmationModal
        isOpen={pendingAction === 'delete'}
        onClose={closeModal}
        onConfirm={handleDelete}
        title="Șterge definitiv contul"
        description={`Ștergerea elimină ireversibil contul ${user.email} și datele asociate. Pentru a confirma, tastează exact adresa de email mai jos.`}
        actionLabel="Șterge definitiv"
        actionVariant="danger"
        submitting={submitting}
        requireTypedConfirmation={user.email}
      />
    </>
  );
};

export default UserDetailDrawer;
